import { useEffect, useRef, type ReactNode } from "react"
import Image from "next/image"

import { CymaticCloud } from "@/app-shell/components/CymaticCloud"
import { Show } from "@clerk/nextjs"

import { SignOutButton } from "@/components/shared/SignOutButton"

interface SignupLayoutProps {
  children: ReactNode
}

export function SignupLayout({ children }: SignupLayoutProps) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const logoRef = useRef<HTMLDivElement>(null)

  // Keeps the header's side inset equal to the logo's distance from the top,
  // so the logo and Sign out hold equal top and side margins as the header
  // scrolls up from its resting offset and pins. Marks the header pinned once
  // content scrolls beneath it, which shows its bottom border.
  useEffect(() => {
    const scroller = scrollerRef.current
    const header = headerRef.current
    const logo = logoRef.current
    if (!scroller || !header || !logo) {
      return
    }
    let frame = 0
    const syncInset = () => {
      frame = 0
      const scrollerTop = scroller.getBoundingClientRect().top
      const inset = logo.getBoundingClientRect().top - scrollerTop
      header.style.paddingInline = `${Math.round(inset)}px`
      const pinnedOverContent =
        scroller.scrollTop > 0 &&
        header.getBoundingClientRect().top - scrollerTop <= 0
      header.toggleAttribute("data-pinned", pinnedOverContent)
    }
    const scheduleSync = () => {
      if (frame === 0) {
        frame = requestAnimationFrame(syncInset)
      }
    }
    syncInset()
    scroller.addEventListener("scroll", scheduleSync, { passive: true })
    window.addEventListener("resize", scheduleSync)
    return () => {
      cancelAnimationFrame(frame)
      scroller.removeEventListener("scroll", scheduleSync)
      window.removeEventListener("resize", scheduleSync)
    }
  }, [])

  return (
    <div className="relative h-screen grid grid-cols-1 md:grid-cols-2 overflow-hidden">
      {/* Left: independently scrollable. The header scrolls up from its
          resting offset, then pins to the top over the scrolling content. */}
      <div className="bg-surface-default relative overflow-hidden">
        <div ref={scrollerRef} className="h-full overflow-y-auto flex flex-col">
          <div aria-hidden className="shrink-0 md:h-16" />
          <header
            ref={headerRef}
            className="sticky top-0 z-10 shrink-0 bg-surface-default border-b border-transparent data-[pinned]:border-coval-border flex items-center justify-between py-6 px-6 md:px-[80px]"
          >
            <div ref={logoRef} className="flex">
              <Image
                src="/coval-logo-vector.svg"
                alt="Coval"
                width={91}
                height={20}
                priority
                className="dark:brightness-0 dark:invert"
              />
            </div>
            <Show when="signed-in">
              <SignOutButton className="dark:text-white dark:hover:text-white" />
            </Show>
          </header>
          <main className="flex-1 flex items-center justify-center pt-8 pb-24 md:pb-[156px]">
            <div className="w-full max-w-[320px] px-6 md:px-0">{children}</div>
          </main>
        </div>
      </div>

      {/* Right: hidden on mobile */}
      <div className="hidden md:block relative overflow-hidden">
        <CymaticCloud />
      </div>
    </div>
  )
}
